import { z } from "zod";
import { documentCategorySchema } from "../documentCategory/document-category.schema";
import { entitySchema } from "../entity.schema";
import { financialAccountSchema } from "../financialAccount.schema";
import { userSchema } from "../user.schema";
import {
    AccrualReviewStatus,
    DocumentTaxonomyExtractionSchema,
    ExtractionStatus,
    ImporterErrors,
    SpreadsheetImporterStatus,
    TransactionsImportStatus,
} from "../../types/financialDocument";

const financialDocumentListItemIntegrationAccountSchema = z.object({
    id: z.string(),
    financialAccountId: z.number().nullable(),
});

const accrualExtractionStatusSchema = z.object({
    status: z.nativeEnum(ExtractionStatus),
});

const financialDocumentListTransactionMatchSchema = z.object({
    id: z.number(),
    financialTransaction: z.object({
        id: z.number(),
        amount: z.number(),
        date: z.string(),
        bankDescription: z.string(),
        displayDescription: z.string().nullable(),
        categoryId: z.string(),
        entityId: z.number(),
        financialAccountId: z.number(),
    }),
});

const financialDocumentListItemScalarsSchema = z.object({
    id: z.number(),
    source: z.string(),
    createdAt: z.string(),
    path: z.string(),
    fileName: z.string(),
    originalFileName: z.string().nullable(),
    hasAttemptedMatching: z.boolean(),
    ocrSuccessful: z.boolean(),
    amount: z.number().nullable(),
    date: z.string().nullable(),
    spreadsheetImporterStatus: z.nativeEnum(SpreadsheetImporterStatus),
    spreadsheetParsingRuleId: z.number().nullable(),
    isSpreadsheet: z.boolean().nullable(),
    numTransactions: z.number().nullable(),
    csvFormatStr: z.string().nullable(),
    entityId: z.number().nullable(),
    financialAccountId: z.number().nullable(),
    integrationAccountId: z.string().nullable(),
    accountInfoExtracted: z.boolean(),
    taxonomyExtraction: DocumentTaxonomyExtractionSchema.nullable(),
    taxonomyExtractionStatus: z.nativeEnum(ExtractionStatus),
    loanExtracted: z.boolean(),
    importerErrors: z.array(z.nativeEnum(ImporterErrors)).nullable(),
    dismissedImporterErrors: z.array(z.nativeEnum(ImporterErrors)).nullable(),
    openingBalance: z.number().nullable(),
    closingBalance: z.number().nullable(),
    openingDate: z.string().nullable(),
    closingDate: z.string().nullable(),
    transactionsSpreadsheetId: z.string().nullable(),
    transactionsImportStatus: z.nativeEnum(TransactionsImportStatus).nullable(),
    externalExtendFileId: z.string().nullable(),
    externalExtendLightParseFileId: z.string().nullable(),
    importerQueue: z.string().nullable(),
    accrualReviewStatus: z.nativeEnum(AccrualReviewStatus).nullable(),
    documentCategoryId: z.string().nullable(),
    counterpartyId: z.string().nullable(),
    cashAppExcludedLinkedAccountPaymentCount: z.number().nullable(),
    isReconciled: z.boolean().optional(),
});

export const financialDocumentListItemSchema =
    financialDocumentListItemScalarsSchema.extend({
        entity: entitySchema
            .pick({
                id: true,
                name: true,
                iconColor: true,
                logo: true,
                isBusiness: true,
                isPersonal: true,
                userId: true,
                workspaceId: true,
                federalTaxRate: true,
                industry: true,
                legalType: true,
            })
            .nullable(),
        financialAccount: financialAccountSchema
            .pick({
                id: true,
                name: true,
                sourceType: true,
                accountNumberMask: true,
                entityId: true,
                institutionName: true,
                financialType: true,
            })
            .nullable(),
        integrationAccount:
            financialDocumentListItemIntegrationAccountSchema.nullable(),
        uploader: userSchema
            .pick({
                id: true,
                name: true,
                type: true,
                avatar: true,
                iconColor: true,
                email: true,
            })
            .nullable(),
        documentCategory: documentCategorySchema.nullable(),
        counterparty: z
            .object({
                id: z.string(),
                name: z.string(),
            })
            .nullable(),
        transactionMatches: z.array(
            financialDocumentListTransactionMatchSchema,
        ),
        documentInvoicesExtraction: accrualExtractionStatusSchema.nullable(),
        documentBillsExtraction: accrualExtractionStatusSchema.nullable(),
    });

export type FinancialDocumentListItemSchema = z.infer<
    typeof financialDocumentListItemSchema
>;

// total/pageCount are omitted when the request sets skipCount
export const fetchFinancialDocumentsResponseSchema = z.object({
    data: z.array(financialDocumentListItemSchema),
    total: z.number().optional(),
    pageCount: z.number().optional(),
});
